import { useSuspenseQuery } from '@apollo/client/react';
import { useMemo, useRef, useState, useTransition } from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { SearchX } from 'lucide-react';
import { debounce } from 'lodash';

import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { withSuspense } from 'src/hoc';
import {
  EscrowMembershipsOrderByEnum,
  QueryOrderEnum,
  InvitationStatusEnum,
  EscrowMembershipRoleEnum,
  UserRoleEnum,
} from 'src/graphql/schema';
import { EscrowMembershipsDocument } from 'src/graphql/actions/escrowMemberships/escrowMemberships.query';
import { ProfileDocument } from 'src/graphql/actions/profile/profile.query';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from 'src/lib/utils';
import Paginator from '@/components/ui/paginator';
import { InputSearch } from '@/components/ui/input-search';
import { EmptyBlock } from '@/components/ui/empty-block';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ActionDropdown,
  MembershipActionsContextProvider,
  useMembershipActions,
} from 'src/containers/Application/Memberships/View';

import { RemoveMembershipDialog, refetchEscrowMemberships } from '../../Management';
import { getMembershipSortPriority } from '../../Management/lib';
import { roleOptionsDict } from '../../Management/model/roleOptions';
import PermissionTooltip from '../../Management/ui/PermissionTooltip';

import EditPermissionDialog from './EditPermissionDialog';

type IMembershipRow = {
  id: string;
  user: {
    id: string;
    name: string;
    role: UserRoleEnum;
    email: string;
    isCurrentUser: boolean;
  };
  role: EscrowMembershipRoleEnum;
  action: {
    canUpdate: boolean;
    canRemove: boolean;
  };
};

const columnHelper = createColumnHelper<IMembershipRow>();
const getColumns = (totalCount: number) => [
  columnHelper.accessor('user', {
    header: `Member (${totalCount})`,
    cell: (props) => {
      const { name, email, role, isCurrentUser } = props.getValue();
      const fallback = name?.charAt(0).toUpperCase();

      return (
        <div className="flex items-center gap-2">
          <Avatar className="size-8.5 rounded-full shrink-0">
            <AvatarImage src={getGravatarUrl(email)} />
            <AvatarFallback className="rounded-full bg-primary text-white">{fallback}</AvatarFallback>
          </Avatar>
          <div className="grow min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-sm text-foreground font-semibold truncate">{name}</div>
              {role === UserRoleEnum.Owner ? (
                <Badge
                  variant="pill"
                  className="border-0 whitespace-nowrap text-2xs bg-foreground text-white py-0.5 px-1.5"
                >
                  Account owner
                </Badge>
              ) : (
                isCurrentUser && (
                  <Badge variant="pill" className="border-0 whitespace-nowrap text-2xs py-0.5 px-1.5">
                    You
                  </Badge>
                )
              )}
            </div>
            <div className="text-sm text-muted-foreground truncate">{email}</div>
          </div>
        </div>
      );
    },
  }),
  columnHelper.accessor('role', {
    header: () => (
      <div className="flex items-center gap-2">
        Permission <PermissionTooltip />
      </div>
    ),
    cell: (props) => <span className="text-sm text-foreground">{roleOptionsDict[props.getValue()]}</span>,
  }),
  columnHelper.accessor('action', {
    header: '',
    meta: {
      headerClassName: 'w-16',
      cellClassName: 'w-16',
    },
    cell: (props) => (
      <ActionDropdown
        id={props.row.original.id}
        canRemove={props.getValue().canRemove}
        canUpdate={props.getValue().canUpdate}
      />
    ),
  }),
];

type Props = {
  beneficiaryId: string;
  escrowInvitationStatus?: InvitationStatusEnum;
};

const MembershipsViewContainerContent = ({ beneficiaryId, escrowInvitationStatus }: Props) => {
  const { removeMembershipId, closeRemoveDialog } = useMembershipActions();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isPending, startTransition] = useTransition();
  const {
    data: { profile },
  } = useSuspenseQuery(ProfileDocument);
  const {
    data: { escrowMemberships },
  } = useSuspenseQuery(EscrowMembershipsDocument, {
    variables: {
      beneficiaryId,
      page,
      perPage: 10,
      orderBy: EscrowMembershipsOrderByEnum.Role,
      order: QueryOrderEnum.Asc,
      query: searchQuery,
    },
  });
  // Unfiltered total for the column header, so searching doesn't change it
  const {
    data: {
      escrowMemberships: { nodesCount: totalMembersCount },
    },
  } = useSuspenseQuery(EscrowMembershipsDocument, {
    variables: {
      beneficiaryId,
      perPage: 0,
    },
  });

  const membersMapped = useMemo(() => {
    const sortedNodes = escrowMemberships.nodes
      .slice()
      .sort(
        (a, b) =>
          getMembershipSortPriority(a.appSubscription.user, profile.id) -
          getMembershipSortPriority(b.appSubscription.user, profile.id),
      );

    return sortedNodes.map(({ id, role, policy, appSubscription: { user } }) => ({
      id,
      user: {
        id: user.id,
        name: user.name || '-',
        role: user.role,
        email: user.email || '-',
        isCurrentUser: user.id === profile.id,
      },
      role,
      action: {
        canUpdate: policy.update,
        canRemove: policy.remove,
      },
    }));
  }, [escrowMemberships.nodes, profile.id]);
  const columns = useMemo(() => getColumns(totalMembersCount), [totalMembersCount]);
  const table = useReactTable({
    data: membersMapped,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const isRejected = escrowInvitationStatus === InvitationStatusEnum.Rejected;

  const onPageChange = (pageNumber: number) => startTransition(() => setPage(pageNumber));

  const debouncedCommitSearch = useRef(
    debounce(
      (value: string) =>
        startTransition(() => {
          setSearchQuery(value);
          setPage(1);
        }),
      500,
    ),
  ).current;

  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => debouncedCommitSearch(event.target.value);

  const onClearSearch = () => {
    debouncedCommitSearch.cancel();
    startTransition(() => {
      setSearchQuery('');
      setPage(1);
    });
  };

  const onRemoveDialogOpenChange = (open: boolean) => {
    if (!open) closeRemoveDialog();
  };

  return (
    <section className="space-y-3">
      <InputSearch
        isLoading={false}
        placeholder="Search name or email"
        className={cn('max-w-75', isRejected && 'pointer-events-none')}
        onChange={onSearch}
        onClear={onClearSearch}
      />
      <Card className={cn('overflow-hidden', isRejected && 'opacity-70 pointer-events-none')}>
        <Table isLoading={isPending}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'bg-gray-50 text-slate-700 font-bold text-xs first:rounded-tl-md last:rounded-tr-md uppercase',
                      header.column.columnDef.meta?.headerClassName,
                    )}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="group/row">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        cell.column.columnDef.meta?.cellClassName,
                        'group-last/row:first:rounded-bl-md group-last/row:last:rounded-br-md',
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length} className="h-4 text-center">
                  <EmptyBlock
                    title="No results"
                    icon={SearchX}
                    withCard={false}
                    classes={{
                      title: 'text-sm',
                    }}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <Paginator
        currentPage={page}
        totalPages={escrowMemberships.pagesCount}
        onPageChange={onPageChange}
        showPreviousNext={true}
        hideOnSinglePage
        className="py-2"
      />
      <RemoveMembershipDialog
        id={removeMembershipId ?? ''}
        open={Boolean(removeMembershipId)}
        onOpenChange={onRemoveDialogOpenChange}
        successCallback={refetchEscrowMemberships}
      />
      <EditPermissionDialog />
    </section>
  );
};

const MembershipsViewContainer = (props: Props) => (
  <MembershipActionsContextProvider>
    <MembershipsViewContainerContent {...props} />
  </MembershipActionsContextProvider>
);

export default withSuspense(MembershipsViewContainer);
